/* eslint-disable sonarjs/no-duplicate-string */
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { FUNDING_SOURCE_TYPES } from "../../../constants";
import {
  isFundingSourceCashAndSecurities,
  isFundingSourceCashOnly,
  isFundingSourceSecuritiesOnly,
} from "../../../helpers";
import useIntakeFormStore from "../../../store/IntakeForm.store";
import { getTaxLotDataInputFields } from "../../../utils/helpers/intakeForm";
import { standardizeKey } from "../../../utils/helpers/standardizer.helper";
import Card from "../../Card";
import DynamicGroupField from "../../Form/DynamicGroupField";
import CashLotTaxInfo from "./CashLotTaxInfo";
import CashTaxLotDataMessage from "./CashTaxLotDataMessage";
import CashTaxLotDisclosure from "./CashTaxLotDisclosure";

const FundingInformationCard = ({
  id,
  headerIcon,
  isEnabled,
  setShowFundingTypeModal,
  setHasConfirmedFundingType,
  setNewSelectedValue,
  hasConfirmedFundingType,
}) => {
  const [formState, formOptions] = useIntakeFormStore((state) => [
    state.data.formState,
    state.data.formOptions,
  ]);
  const { control, setValue } = useFormContext();

  const [isCashOnly, setIsCashOnly] = useState(false);
  const [isSecuritiesOnly, setIsSecuritiesOnly] = useState(false);
  const [fundingSource, setFundingSource] = useState([]);

  const watchedFundingSrc = useWatch({
    control,
    name: "fundingSrc",
  });
  const watchedTemplateTaxLot = useWatch({
    control,
    name: "taxLotData",
  });
  const watchedCashAmount = useWatch({
    control,
    name: "cashAmount",
  });

  useEffect(() => {
    if (watchedFundingSrc && watchedFundingSrc !== "") {
      setIsCashOnly(isFundingSourceCashOnly(watchedFundingSrc));
      setIsSecuritiesOnly(isFundingSourceSecuritiesOnly(watchedFundingSrc));
    }
  }, [watchedFundingSrc]);

  useEffect(() => {
    if (hasConfirmedFundingType) {
      setHasConfirmedFundingType(false);
    }
  }, [hasConfirmedFundingType, setHasConfirmedFundingType]);

  useEffect(() => {
    if (!formState?.strategyDetail) return;

    const { sector } = formState.strategyDetail;
    setFundingSource(
      formOptions.fundingSource.map((ele) =>
        sector === "Treasury Bonds"
          ? {
              ...ele,
              disabled: [
                standardizeKey(FUNDING_SOURCE_TYPES.cashAndSecurities),
                standardizeKey(FUNDING_SOURCE_TYPES.securitiesOnly),
              ].includes(standardizeKey(ele.label)),
            }
          : {
              ...ele,
            }
      )
    );
    sector === "Treasury Bonds" &&
      setValue("fundingSrc", FUNDING_SOURCE_TYPES.cashOnly);
  }, [formState.strategyDetail, formOptions.fundingSource, setValue]);

  useEffect(() => {
    let fundingSrc = ``;
    const cashAmount = watchedCashAmount;

    const isCashWithEmptyTaxLot =
      watchedTemplateTaxLot.length > 0 &&
      watchedTemplateTaxLot[0].cusip === "" &&
      watchedTemplateTaxLot[0].qty === "";

    if (
      cashAmount > 0 &&
      (isCashWithEmptyTaxLot || watchedTemplateTaxLot.length === 0)
    ) {
      fundingSrc = FUNDING_SOURCE_TYPES.cashOnly;
    } else if (watchedTemplateTaxLot.length > 0 && cashAmount > 0) {
      fundingSrc = FUNDING_SOURCE_TYPES.cashAndSecurities;
    } else if (
      watchedTemplateTaxLot.length > 0 &&
      (!cashAmount || cashAmount === "" || cashAmount === 0)
    ) {
      fundingSrc = FUNDING_SOURCE_TYPES.securitiesOnly;
    }

    setValue(`fundingSrc`, fundingSrc);
  }, [setValue, watchedCashAmount, watchedTemplateTaxLot]);

  const isLocked = formState.isFormLocked;

  const taxLotDataInputFields = getTaxLotDataInputFields(formState);

  const shouldShowConfirmation = (prevValue, value) => {
    return (
      isFundingSourceCashAndSecurities(prevValue) ||
      (isFundingSourceCashOnly(prevValue) &&
        isFundingSourceSecuritiesOnly(value)) ||
      (isFundingSourceSecuritiesOnly(prevValue) &&
        isFundingSourceCashOnly(value))
    );
  };

  const triggerConfirmationModal = (prevValue, value) => {
    const showModal = shouldShowConfirmation(prevValue, value);

    //show confirmation modal
    setShowFundingTypeModal(showModal);

    return showModal;
  };

  const fundingSourceInput = {
    type: "select",
    name: "fundingSrc",
    label: "Funding Type",
    options: {
      required: true,
      readOnly: isLocked,
      items: fundingSource || [],
    },
    confirmOptions: {
      withConfirmation: true,
      hasConfirmed: hasConfirmedFundingType,
      triggerConfirmationModal: triggerConfirmationModal,
      setNewSelectedValue: setNewSelectedValue,
    },
  };

  const taxLotDataTooltipContent = () => (
    <p className={`tooltip__content-body-text`}>
      Important Note: Any matured bonds will be treated as cash and invested
      toward the model portfolio.
    </p>
  );

  return (
    <Card
      title="Funding Information"
      id={id}
      openByDefault={isEnabled}
      disabled={!isEnabled}
      errorCount={
        formState.taxLotData.totalErrorCount +
        formState.accountRestrictionData.totalErrorCount
      }
      headerIcon={headerIcon}
      subtitle="Required Fields"
    >
      {/* <div className="card__funding-source-selection">
        <SelectInput {...fundingSourceInput} />
      </div> */}
      {isEnabled && (
        <div className="card__content">
          {/* {watchedFundingSrc && watchedFundingSrc !== "" && ( */}
          <div className="content_wrapper">
            {/* {!isSecuritiesOnly &&  */}
            <CashLotTaxInfo isLocked={isLocked} />
            {/* } */}
            {/* {!isCashOnly && ( */}
            <>
              <CashTaxLotDisclosure />
              <DynamicGroupField
                title="Enter Tax Lot Data"
                fieldArrayName="taxLotData"
                store="intake"
                inputFields={taxLotDataInputFields}
                tooltip={{
                  heading: "Tax Lot Data",
                  content: taxLotDataTooltipContent(),
                  direction: "right",
                }}
                disabled={isLocked}
                isRequired={false}
                importantMessage={<CashTaxLotDataMessage />}
                displayTotal={true}
                quantityUnitLabel={
                  formState?.strategyDetail?.assetClass === "Equity"
                    ? "Number of shares"
                    : "Par value"
                }
              />
            </>
            {/* )} */}
          </div>
          {/* )} */}
        </div>
      )}
    </Card>
  );
};

FundingInformationCard.propTypes = {
  id: PropTypes.string,
  headerIcon: PropTypes.string,
  isEnabled: PropTypes.bool,
  setShowFundingTypeModal: PropTypes.func,
  setHasConfirmedFundingType: PropTypes.func,
  setNewSelectedValue: PropTypes.func,
  hasConfirmedFundingType: PropTypes.bool,
};

export default FundingInformationCard;
